"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./MMVPriceBreakup.module.scss";
import getIndianCurrencyFormat, {
  getIndianCurrencyFormatForMaskedPrice,
} from "@helpers/getIndianCurrencyFormat";
import { formatCurrencyINR } from "@utils/numberFormatter";
import Text from "@components/atoms/Text";
import Button from "@components/atoms/Button";
import InsuranceCoverInfoModal from "@components/molecules/InsuranceCoverInfoModal";
import {
  getInsuranceCoverContent,
  getInsurancePlanSelectedFromCover,
  INSURANCE_COVER,
  type InsuranceCover,
  type PricingInsuranceCoverOption,
} from "@constants/insuranceCover";
import assetLoader from "@utils/internalImageLoder";
import { cn } from "@utils/utils";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { LoginSdkConfig } from "@enums/LoginSdkConfig";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { PageType } from "@constants/pageTypes";
import { useGlobalLoader } from "@hooks/useGlobalLoader";

interface PriceBreakdownProps {
  exShowroomPrice: string;
  otherCharges: any;
  otherChargesTotal: string;
  onRoadPrice: number;
  ackoDiscount: string;
  ackoDrivePrice: string;
  deliveryDate: string;
  cityName: string;
  isLoggedIn: boolean;
  setIsModalOpen: (val: boolean) => void;
  setIsIntentModalOpen: (val: boolean) => void;
  isInventoryAvailable: boolean;
  deliveryType: string;
  isSellable: boolean;
  pageType?: PageType;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  carAvailability?: string;
  savingsBreakup?: any;
  selectedColor?: any;
  savingBalance?: any;
  environmentCompensation?: number;
  fuelType?: string;
  selectedYear: string;
  onYearChange: (year: string) => void;
  availableYears?: string[];
  insuranceCovers?: PricingInsuranceCoverOption[];
  selectedInsuranceCover: InsuranceCover;
  onInsuranceCoverChange: (cover: InsuranceCover) => void;
  onGetQuoteWithInsurance: () => void;
  isUpsellFlow?: boolean;
  onPriceBreakupEndReachedChange?: (hasPassed: boolean) => void;
}

interface ChargeRow {
  label: string;
  value: string;
}

const getChargeAmount = (value: string) => Number(value) || 0;

export default function PriceBreakupV2({
  exShowroomPrice,
  otherCharges,
  onRoadPrice,
  ackoDiscount,
  ackoDrivePrice,
  deliveryDate,
  cityName,
  isLoggedIn,
  isInventoryAvailable,
  deliveryType,
  setIsModalOpen,
  setIsIntentModalOpen,
  isSellable,
  pageType,
  brandName,
  modelName,
  variantName,
  carAvailability,
  savingsBreakup,
  selectedColor,
  savingBalance,
  environmentCompensation,
  fuelType,
  selectedYear,
  onYearChange,
  availableYears,
  insuranceCovers = [],
  selectedInsuranceCover,
  onInsuranceCoverChange,
  onGetQuoteWithInsurance,
  isUpsellFlow = false,
  onPriceBreakupEndReachedChange,
}: PriceBreakdownProps) {
  const [isInsuranceInfoModalOpen, setIsInsuranceInfoModalOpen] = useState(false);
  const priceBreakupEndRef = useRef<HTMLDivElement | null>(null);
  const { consumerdiscount: consumerDiscount } = savingsBreakup || {};

  const { showLoader, hideLoader } = useGlobalLoader();

  const effectiveInsuranceCover = isUpsellFlow
    ? INSURANCE_COVER.STANDARD
    : selectedInsuranceCover;
  const selectedInsuranceContent = useMemo(
    () => getInsuranceCoverContent(effectiveInsuranceCover),
    [effectiveInsuranceCover]
  );
  const availableInsuranceCovers = useMemo(
    () => insuranceCovers.filter((cover) => cover.available !== false),
    [insuranceCovers]
  );
  const hasMultipleInsuranceCovers =
    availableInsuranceCovers.length > 1;
  const hasExtendedInsuranceOption = useMemo(
    () =>
      !isUpsellFlow &&
      availableInsuranceCovers.some(
        (cover) => cover.cover_type === INSURANCE_COVER.EXTENDED
      ),
    [availableInsuranceCovers, isUpsellFlow]
  );
  const standardCover = useMemo(
    () =>
      availableInsuranceCovers.find(
        (cover) => cover.cover_type === INSURANCE_COVER.STANDARD
      ),
    [availableInsuranceCovers]
  );
  const extendedCover = useMemo(
    () =>
      availableInsuranceCovers.find(
        (cover) => cover.cover_type === INSURANCE_COVER.EXTENDED
      ),
    [availableInsuranceCovers]
  );
  const additionalDiscountWithExtended = useMemo(() => {
    const standardDiscount = Number(standardCover?.acko_drive_savings || 0);
    const extendedDiscount = Number(extendedCover?.acko_drive_savings || 0);

    if (extendedDiscount <= standardDiscount) {
      return 0;
    }

    return extendedDiscount - standardDiscount;
  }, [extendedCover?.acko_drive_savings, standardCover?.acko_drive_savings]);
  const getRIPPlusConsumer = (savingsBreakup: {
    ripdiscount: any;
    consumerdiscount: any;
  }) => {
    return savingsBreakup?.ripdiscount + savingsBreakup?.consumerdiscount || 0;
  };
  const visibleChargeRows = useMemo(
    () =>
      otherCharges.filter((charge: ChargeRow) => {
        const normalized = charge.label.toLowerCase();
        return (
          normalized !== "on road price" &&
          normalized !== "on-road price" &&
          normalized !== "acko drive discount" &&
          normalized !== "consumer savings"
        );
      }),
    [otherCharges]
  );
  const registrationChargeRows = useMemo(
    () =>
      visibleChargeRows.filter((charge: ChargeRow) =>
        charge.label.toLowerCase().includes("registration")
      ),
    [visibleChargeRows]
  );
  const insuranceChargeRows = useMemo(
    () =>
      visibleChargeRows.filter((charge: ChargeRow) =>
        charge.label.toLowerCase().includes("insurance")
      ),
    [visibleChargeRows]
  );
  const miscChargeRows = useMemo(
    () =>
      [...visibleChargeRows]
        .filter((charge: ChargeRow) => {
          const normalized = charge.label.toLowerCase();
          return (
            !normalized.includes("registration") &&
            !normalized.includes("insurance")
          );
        })
        .sort(
          (firstCharge, secondCharge) =>
            getChargeAmount(secondCharge.value) -
            getChargeAmount(firstCharge.value)
        ),
    [visibleChargeRows]
  );

  const getChargeLabelNode = (label: string) => {
    if (label.toLowerCase().includes("insurance")) {
      return (
        <span className={styles.chargeLabel}>
          Dealer Insurance -{" "}
          <span className={styles.chargeLabelStrong}>
            {effectiveInsuranceCover === INSURANCE_COVER.EXTENDED
              ? "Extended"
              : "Standard"}
          </span>
        </span>
      );
    }

    return <span className={styles.chargeLabel}>{label}</span>;
  };

  const renderChargeRows = (rows: ChargeRow[]) => {
    return rows.map((charge, index) => (
      <div
        key={`${charge.label}-${index}`}
        className={styles.chargeRowInline}
      >
        {getChargeLabelNode(charge.label)}
        <span className={styles.chargeValue}>
          {formatCurrencyINR(charge.value)}
        </span>
      </div>
    ));
  };

  const getPlanSelectedLabel = (cover: InsuranceCover) =>
    getInsurancePlanSelectedFromCover(cover);

  const handlePriceBreakupCta = async () => {
    showLoader();
    if (isLoggedIn) {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_get_detailed_quote",
          plan_selected: getPlanSelectedLabel(effectiveInsuranceCover),
          car_availability: carAvailability,
          vehicle_make: brandName,
          vehicle_model: modelName,
          vehicle_variant: variantName,
          vehicle_color: selectedColor?.brand_color,
          city_name: cityName,
          section: "insurance",
        },
      });
      onGetQuoteWithInsurance();
      return;
    }

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_login_to_save",
        plan_selected: getPlanSelectedLabel(effectiveInsuranceCover),
        car_availability: carAvailability,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        vehicle_color: selectedColor?.brand_color,
        city_name: cityName,
        section: "Price breakup",
      },
    });

    const mmvDetails = {
      make: brandName || "",
      model: modelName || "",
      varaint: variantName || "",
      color: selectedColor?.brand_color || "",
    };

    if (typeof window !== "undefined") {
      sessionStorage.setItem("loginType", "Reveal price");
      sessionStorage.setItem("flowType", "New Car");
      localStorage.setItem(
        "MMV",
        JSON.stringify({
          make: brandName || "",
          model: modelName || "",
          variant: variantName || "",
          color: selectedColor?.brand_color || "",
        })
      );
    }

    const loginUrl = getLoginUrl(
      addCaptureIntentIfNeeded(
        window.location.href,
        mmvDetails,
        "Reveal price",
        pageType
      )
    );

    try {
      await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
    } catch (error) {
      console.error("Error redirecting to quote login:", error);
      hideLoader();
    }
  };

  useEffect(() => {
    if (!onPriceBreakupEndReachedChange || typeof window === "undefined") {
      return;
    }

    const updatePriceBreakupPosition = () => {
      const rect = priceBreakupEndRef.current?.getBoundingClientRect();
      onPriceBreakupEndReachedChange(Boolean(rect && rect.top <= 0));
    };

    updatePriceBreakupPosition();
    window.addEventListener("scroll", updatePriceBreakupPosition, {
      passive: true,
    });
    window.addEventListener("resize", updatePriceBreakupPosition);

    return () => {
      window.removeEventListener("scroll", updatePriceBreakupPosition);
      window.removeEventListener("resize", updatePriceBreakupPosition);
    };
  }, [onPriceBreakupEndReachedChange]);

  return (
    <div
      className={cn(styles.container, isUpsellFlow && styles.upsellContainer)}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>Price breakup</h2>
        {/* <div className={styles.languageSelector}>
          <button
            className={`${styles.languageButton} ${
              selectedLanguage === "KA" ? styles.active : ""
            }`}
            onClick={() => setSelectedLanguage("KA")}
          >
            KA
          </button>
          <button
            className={`${styles.languageButton} ${
              selectedLanguage === "BH" ? styles.active : ""
            }`}
            onClick={() => setSelectedLanguage("BH")}
          >
            BH
          </button>
        </div> */}
        {isLoggedIn && availableYears && availableYears.length > 1 && (
          <div className={styles.yearSelector}>
            {availableYears.map((year) => (
              <button
                className={`${styles.yearButton} ${
                  selectedYear === year ? styles.yearButtonActive : ""
                }`}
                onClick={() => onYearChange(year)}
                key={year}
              >
                {year}
              </button>
            ))}
          </div>
        )}
      </div>

      {hasExtendedInsuranceOption && (
        <>
          <div className={styles.insuranceSegmentedControl}>
            {availableInsuranceCovers.map((cover) => {
              const isSelected = cover.cover_type === effectiveInsuranceCover;

              return (
                <button
                  type="button"
                  key={cover.cover_type}
                  className={`${styles.insuranceSegmentButton} ${
                    isSelected ? styles.insuranceSegmentButtonSelected : ""
                  }`}
                  onClick={async () => {
                    await trackEventWithCommonProps(
                      "action",
                      EventType.TRACK_EVENT,
                      {
                        pageType,
                        additionalProps: {
                          action_type: "tap_btn",
                          action_name: "tap_btn_insurance_plan",
                          plan_selected: getPlanSelectedLabel(
                            cover.cover_type
                          ),
                          car_availability: carAvailability,
                          vehicle_make: brandName,
                          vehicle_model: modelName,
                          vehicle_variant: variantName,
                          vehicle_color: selectedColor?.brand_color,
                          city_name: cityName,
                        },
                      }
                    );
                    onInsuranceCoverChange(cover.cover_type);
                  }}
                >
                  <Text
                    className={`${styles.insuranceSegmentLabel} ${
                      cover.cover_type === INSURANCE_COVER.STANDARD
                        ? styles.insuranceSegmentLabelStandard
                        : ""
                    }`}
                  >
                    {cover.label ||
                      (cover.cover_type === INSURANCE_COVER.EXTENDED
                        ? "Extended cover"
                        : "Standard cover")}
                  </Text>
                  <Text className={styles.insuranceSegmentProtection}>
                    {cover.protection_text ||
                      getInsuranceCoverContent(cover.cover_type).protectionText}
                  </Text>
                </button>
              );
            })}
          </div>

          <div
            className={`${styles.insuranceInfoBanner} ${
              effectiveInsuranceCover === INSURANCE_COVER.EXTENDED
                ? styles.insuranceInfoBannerCompact
                : ""
            }`}
          >
            <Text className={styles.insuranceInfoText}>
              <span className={styles.insuranceInfoHighlight}>
                {selectedInsuranceContent.mmvSummary.highlight}
              </span>{" "}
              {selectedInsuranceContent.mmvSummary.body}{" "}
              <button
                type="button"
                className={styles.knowMoreInlineButton}
                onClick={() => setIsInsuranceInfoModalOpen(true)}
              >
                Know more
              </button>
            </Text>
          </div>
        </>
      )}

      <div className={styles.priceCard}>
        <div className={styles.priceComponents}>
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>Ex-showroom price</span>
            <span className={styles.priceValue}>
              {`₹${getIndianCurrencyFormat(exShowroomPrice)}`}
            </span>
          </div>

          {renderChargeRows(registrationChargeRows)}

          {insuranceChargeRows.length > 0 && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.chargeRows}>
                {renderChargeRows(insuranceChargeRows)}
              </div>
            </>
          )}

          {miscChargeRows.length > 0 && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.chargeRows}>
                {renderChargeRows(miscChargeRows)}
              </div>
            </>
          )}

          {consumerDiscount > 0 && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>Consumer Savings</span>
                <span className={styles.priceValue}>
                  {`₹${getIndianCurrencyFormat(consumerDiscount)}`}
                </span>
              </div>
            </>
          )}

          <div className={styles.divider}></div>
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>
              {`On-road price in ${cityName}`}
            </span>
            <span
              className={`${styles.priceValue} ${styles.priceValuePrimary}`}
            >
              {`₹${getIndianCurrencyFormat(
                isLoggedIn
                  ? Number(onRoadPrice || 0) -
                      getRIPPlusConsumer(savingsBreakup)
                  : onRoadPrice
              )}`}
            </span>
          </div>

          {isSellable && (
            <div className={styles.discountBlock}>
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>ACKO Drive discount</span>
                <span className={styles.discountValue}>
                  {`-₹${
                    isLoggedIn
                      ? getIndianCurrencyFormat(savingsBreakup?.ackodiscount || 0)
                      : getIndianCurrencyFormatForMaskedPrice(
                          savingsBreakup?.ackodiscount || 0
                        )
                  }`}
                </span>
              </div>
              {effectiveInsuranceCover === INSURANCE_COVER.STANDARD &&
                additionalDiscountWithExtended > 0 && (
                  <Text className={styles.extraSavingsText}>
                    Save{" "}
                    {isLoggedIn
                      ? formatCurrencyINR(additionalDiscountWithExtended)
                      : `₹${getIndianCurrencyFormatForMaskedPrice(
                          additionalDiscountWithExtended
                        )}`}{" "}
                    more with extended cover
                  </Text>
                )}
            </div>
          )}

          {savingBalance > 0 && (
            <Text className={styles.savingBalanceText}>
              (
              <span className={styles.savingBalanceValue}>
                {formatCurrencyINR(savingBalance)}
              </span>{" "}
              New car savings balance applied)
            </Text>
          )}
        </div>
        {isSellable && (
          <div className={styles.ackoPriceContainer}>
            <div className={styles.ackoPriceRow}>
              <span
                className={styles.ackoPriceLabel}
              >{`ACKO Drive price`}</span>
              <span className={styles.ackoPriceValue}>
                {`₹${
                  isLoggedIn
                    ? getIndianCurrencyFormat(ackoDrivePrice)
                    : getIndianCurrencyFormatForMaskedPrice(ackoDrivePrice)
                }`}
              </span>
            </div>
            {isSellable &&
              (fuelType?.toLowerCase() === "electric" ? (
                <div className={styles.gstSavingsText}>
                  No GST impact on EV cars, prices are the same before and
                  after.
                </div>
              ) : (
                environmentCompensation &&
                environmentCompensation > 1 && (
                  <div className={styles.gstSavingsText}>
                    Good news! This price already includes your GST savings of ₹
                    {getIndianCurrencyFormat(environmentCompensation)}
                  </div>
                )
              ))}

            <div className={styles.infoBox}>
              <p className={styles.infoText}>
                <span className={styles.infoLabel}>{`Did you know? `}</span>
                {`Our prices are lower than dealership price because we don't charge any commissions`}
              </p>
            </div>
          </div>
        )}
      </div>
      {isSellable && (
        <p className={styles.disclaimer}>
          Prices are subject to change based on your delivery date
        </p>
      )}

      {availableInsuranceCovers.length > 0 && (
        <Button
          className={styles.insuranceContinueButton}
          onClick={handlePriceBreakupCta}
        >
          {isLoggedIn
            ? hasMultipleInsuranceCovers
              ? `Get quote with ${selectedInsuranceContent.label}`
              : "Get quote"
            : "Unlock ACKO Drive price"}
        </Button>
      )}

      <InsuranceCoverInfoModal
        isOpen={isInsuranceInfoModalOpen}
        onClose={() => setIsInsuranceInfoModalOpen(false)}
      />
      <div ref={priceBreakupEndRef} />
    </div>
  );
}
